// ============================================================================
// SupplyOrderCard — editable proposal card for propose_supply_order.
// "Do it →" inserts into supply_requests (status='Submitted') and updates
// the audit row in copilot_actions. Mirrors FeedbackCard / TaskCard chrome.
//
// Supply metadata (item_name, unit, unit_cost, sds_required, sds_drive_url)
// is enriched live from the supplies table when supply_id is set, so the
// AM sees pricing + SDS state regardless of what the agent included in the
// proposal payload.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Package } from 'lucide-react';
import { supabase } from '../../../shared/supabase';
import { URGENCY_STYLES } from '../../../shared/design-tokens';
import type { CopilotProposalBase } from '../../../shared/types';
import { CardShell } from './card-shell';
import { FacilityCombobox } from './facility-combobox';
import { optionsFor } from './card-options';

// Urgency is the database's own list (supply_requests_urgency_check), sent with the draft.

interface FacilityOpt {
  facility_id: string;
  facility_name: string;
}

interface SupplyOrderCardProps {
  proposal: CopilotProposalBase;
  facilities: FacilityOpt[];
  isCommitting: boolean;
  onDoIt: (
    edited_input: Record<string, unknown>,
    was_edited: boolean,
  ) => void;
  onDiscard: () => void;
}

interface SupplyMeta {
  supply_id: string;
  item_name: string | null;
  supply_category: string | null;
  unit: string | null;
  unit_cost: number | null;
  preferred_vendor: string | null;
  sds_required: boolean | null;
  sds_drive_url: string | null;
}

export function SupplyOrderCard({
  proposal,
  facilities,
  isCommitting,
  onDoIt,
  onDiscard,
}: SupplyOrderCardProps) {
  const initial = proposal.input as {
    facility_id?: string;
    supply_id?: string | null;
    item_description?: string | null;
    quantity?: number;
    unit_of_measure?: string | null;
    urgency?: 'Emergency' | 'Soon' | 'Planned';
    date_needed?: string | null;
    notes?: string | null;
  };

  const [facility_id, setFacilityId] = useState(initial.facility_id ?? '');
  const [supply_id, setSupplyId] = useState<string | null>(
    initial.supply_id ?? null,
  );
  const [item_description, setItemDescription] = useState(
    initial.item_description ?? '',
  );
  const [quantity, setQuantity] = useState<number>(
    Number(initial.quantity ?? 1) || 1,
  );
  const [unit_of_measure, setUnit] = useState(initial.unit_of_measure ?? '');
  const [urgency, setUrgency] = useState<string>(initial.urgency ?? 'Soon');
  const urgencies = optionsFor(proposal.input, 'urgency', initial.urgency);
  const [date_needed, setDateNeeded] = useState(initial.date_needed ?? '');
  const [notes, setNotes] = useState(initial.notes ?? '');
  const [meta, setMeta] = useState<SupplyMeta | null>(null);

  // Enrich supply metadata client-side when supply_id is set. Best-effort —
  // a fetch failure simply drops the pricing + SDS preview and the AM can
  // still commit.
  useEffect(() => {
    let cancelled = false;
    if (!supply_id) {
      setMeta(null);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('supplies')
        .select(
          'supply_id, item_name, supply_category, unit, unit_cost, preferred_vendor, sds_required, sds_drive_url',
        )
        .eq('supply_id', supply_id)
        .maybeSingle();
      if (cancelled) return;
      if (data) {
        setMeta(data as SupplyMeta);
        // Auto-fill unit if blank and the supply has one.
        if (!unit_of_measure && data.unit) {
          setUnit(data.unit as string);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // unit_of_measure intentionally excluded — we only auto-fill once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supply_id]);

  const wasEdited = useMemo(() => {
    return (
      facility_id !== (initial.facility_id ?? '') ||
      (supply_id ?? null) !== (initial.supply_id ?? null) ||
      (item_description || '') !== (initial.item_description ?? '') ||
      Number(quantity) !== Number(initial.quantity ?? 1) ||
      (unit_of_measure || '') !== (initial.unit_of_measure ?? '') ||
      urgency !== (initial.urgency ?? 'Soon') ||
      (date_needed || '') !== (initial.date_needed ?? '') ||
      (notes || '') !== (initial.notes ?? '')
    );
  }, [
    facility_id,
    supply_id,
    item_description,
    quantity,
    unit_of_measure,
    urgency,
    date_needed,
    notes,
    initial,
  ]);

  const handleDoIt = () => {
    onDoIt(
      {
        facility_id,
        supply_id: supply_id || null,
        item_description: item_description.trim() || null,
        quantity: Number(quantity),
        unit_of_measure: unit_of_measure.trim() || null,
        urgency,
        date_needed: date_needed || null,
        notes: notes.trim() || null,
      },
      wasEdited,
    );
  };

  const estimatedCost = useMemo(() => {
    if (!meta?.unit_cost || !quantity) return null;
    const total = Number(meta.unit_cost) * Number(quantity);
    if (!Number.isFinite(total)) return null;
    return total;
  }, [meta?.unit_cost, quantity]);

  const sdsWarning =
    !!meta &&
    meta.sds_required === true &&
    (!meta.sds_drive_url || meta.sds_drive_url.length === 0);

  const accentByUrgency =
    urgency === 'Emergency'
      ? 'border-st-danger-border-strong'
      : urgency === 'Soon'
      ? 'border-st-warn-border-strong'
      : 'border-st-ok-border-strong';

  const displayItem =
    meta?.item_name ||
    initial.item_description ||
    item_description ||
    'New item';

  return (
    <CardShell
      title="Supply order"
      icon={<Package className="h-4 w-4" />}
      accentClass={accentByUrgency}
      status={proposal.status}
      committedLabel={
        proposal.target_row_id ? `Saved as ${proposal.target_row_id}` : 'Saved'
      }
      isCommitting={isCommitting}
      onDoIt={handleDoIt}
      onDiscard={onDiscard}
    >
      <Field label="Facility">
        <FacilityCombobox
          value={facility_id}
          options={facilities}
          onChange={setFacilityId}
        />
      </Field>

      <Field label="Item">
        {supply_id ? (
          <div className="space-y-1">
            <div className="rounded-md border border-tcs-border bg-tcs-bg-muted px-2 py-1.5 text-sm text-tcs-text">
              {displayItem}
            </div>
            <div className="flex items-center justify-between text-xs text-tcs-text-muted">
              <span>
                {supply_id}
                {meta?.unit_cost
                  ? ` · $${Number(meta.unit_cost).toFixed(2)}/${meta.unit ?? 'unit'}`
                  : ''}
              </span>
              <button
                type="button"
                onClick={() => {
                  setSupplyId(null);
                  setItemDescription(meta?.item_name ?? displayItem);
                }}
                className="text-tcs-text-muted underline-offset-2 hover:text-tcs-text hover:underline"
              >
                edit
              </button>
            </div>
          </div>
        ) : (
          <input
            type="text"
            value={item_description}
            onChange={(e) => setItemDescription(e.target.value)}
            placeholder="Describe the item (size, brand, etc.)"
            className={inputClass}
          />
        )}
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Quantity">
          <input
            type="number"
            min={1}
            step={1}
            value={quantity}
            onChange={(e) =>
              setQuantity(Math.max(1, Number(e.target.value) || 1))
            }
            className={inputClass}
          />
        </Field>
        <Field label="Unit">
          <input
            type="text"
            value={unit_of_measure}
            onChange={(e) => setUnit(e.target.value)}
            placeholder="case / box / each"
            className={inputClass}
          />
        </Field>
      </div>

      <Field label="Urgency">
        <div className="flex gap-1">
          {urgencies.map((u) => {
            const style = URGENCY_STYLES[u as keyof typeof URGENCY_STYLES] ?? { ...URGENCY_STYLES.Planned, label: u };
            const active = urgency === u;
            return (
              <button
                key={u}
                type="button"
                onClick={() => setUrgency(u)}
                className="flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors"
                style={
                  active
                    ? { backgroundColor: style.fill, color: style.text }
                    : { backgroundColor: '#F1F5F9', color: '#475569' }
                }
              >
                {style.label}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="Date needed (optional)">
        <input
          type="date"
          value={date_needed ?? ''}
          onChange={(e) => setDateNeeded(e.target.value)}
          className={inputClass}
        />
      </Field>

      <Field label="Notes (optional)">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className={`${inputClass} resize-none`}
          placeholder="Where to ship, who to contact, why…"
        />
      </Field>

      {(estimatedCost !== null || sdsWarning) && (
        <div className="space-y-2 border-t border-tcs-border pt-3">
          {estimatedCost !== null && (
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium uppercase tracking-wide text-tcs-text-muted">
                Estimated cost
              </span>
              <span className="font-mono text-tcs-text">
                {estimatedCost.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                })}
              </span>
            </div>
          )}
          {sdsWarning && (
            <div
              className="flex items-start gap-2 rounded-md bg-st-warn-bg px-2 py-1.5 text-xs text-st-warn-fg-strong"
              title="This chemical has sds_required=true but no SDS PDF link is on file. The commit will still go through; an admin should attach the SDS soon."
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none" />
              <span>
                No SDS on file for this chemical. Order will still go through;
                please follow up with ops to attach the SDS.
              </span>
            </div>
          )}
        </div>
      )}
    </CardShell>
  );
}

const inputClass =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-text-muted focus:outline-none focus:ring-1 focus:ring-tcs-text-muted';

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
        {label}
      </span>
      {children}
    </label>
  );
}
